import React from "react";
import { useTranslation } from "react-i18next";
import { StyledLabel, StyledTextInput } from "@aureskonnect/react-ui";
import { AvGroup } from "availity-reactstrap-validation";
import { Input } from "antd";
import classnames from "classnames";

import HidePasswordSvgIcon from "@components/Common/SvgIcons/HidePasswordSvgIcon";
import ShowPasswordSvgIcon from "@components/Common/SvgIcons/ShowPasswordSvgIcon";

import "./index.css";

const ERROR_TEXT_STYLE: React.CSSProperties = {
    width: "100%",
    marginTop: "0.25rem",
    fontSize: "80%",
    color: "#f46a6a",
};

const PASSWORD_INPUT_STYLE: React.CSSProperties = {
    height: "38px",
    borderRadius: "6px",
    color: "#2b2828",
    fontSize: "16px",
    fontFamily: "Segoe UI",
};

type FidelityZerosixType = {
    password: string;
    setPassword: (v: string) => void;
    user: string;
    setUser: (v: string) => void;
    inputUser: boolean;
    setInputUser: (v: boolean) => void;
    inputPassword: boolean;
    setInputPassword: (v: boolean) => void;
    setIsEdited: (v: boolean) => void;
    setDashboardLink: (v: string) => void;
    dashboardLink: string;
    apiKey: string;
    setApiKey: (v: string) => void;
    inputApiKey: boolean;
    inputDashboardLink: boolean;
    setInputApiKey: (v: boolean) => void;
    setInputDashboardLink: (v: boolean) => void;
    isConsult?: boolean;
};
export default function FidelityZerosix({
    password,
    setPassword,
    user,
    setUser,
    inputUser,
    setInputUser,
    inputPassword,
    setInputPassword,
    setIsEdited,
    setDashboardLink,
    dashboardLink,
    apiKey,
    setApiKey,
    inputDashboardLink,
    inputApiKey,
    setInputApiKey,
    setInputDashboardLink,
    isConsult,
}: FidelityZerosixType) {
    const { t } = useTranslation();

    const makeHandler = (
        setter: (v: string) => void,
        clearInput: (v: boolean) => void
    ) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setter(e.target.value.trimStart());
        clearInput(false);
        setIsEdited(true);
    };

    const handleUserChange = makeHandler(setUser, setInputUser);
    const handlePasswordChange = makeHandler(setPassword, setInputPassword);
    const handleDashboardLinkChange = makeHandler(
        setDashboardLink,
        setInputDashboardLink
    );
    const handleApiKeyChange = makeHandler(setApiKey, setInputApiKey);

    const groupClassName = ` ${isConsult ? "pe-none__clz" : ""}`;

    return (
        <React.Fragment>
            <AvGroup className={groupClassName}>
                <StyledLabel className="required__clz mt-3" htmlFor="name">
                    {t("User")}
                </StyledLabel>
                <StyledTextInput
                    data-testid="fidelity-franchise-zerosix-user-input"
                    autoComplete="new-password"
                    id="User"
                    name="User"
                    placeholder={t("Write")}
                    type="text"
                    onChange={handleUserChange}
                    className={classnames({ input__clz: inputUser })}
                    value={user}
                />
                {inputUser ? (
                    <div style={ERROR_TEXT_STYLE}>
                        {t("Please enter a user")}
                    </div>
                ) : null}
            </AvGroup>
            <AvGroup className={groupClassName}>
                <StyledLabel className="required__clz mt-3" htmlFor="name">
                    {t("Password")}
                </StyledLabel>
                <Input.Password
                    data-testid="fidelity-franchise-zerosix-password-input"
                    autoComplete="new-password"
                    placeholder={t("Write")}
                    iconRender={(visible) =>
                        visible ? (
                            <ShowPasswordSvgIcon />
                        ) : (
                            <HidePasswordSvgIcon />
                        )
                    }
                    onChange={handlePasswordChange}
                    value={password}
                    className={classnames({ input__clz: inputPassword })}
                    style={PASSWORD_INPUT_STYLE}
                />
                {inputPassword ? (
                    <div style={ERROR_TEXT_STYLE}>
                        {t("Please enter a password")}
                    </div>
                ) : null}
            </AvGroup>
            <AvGroup className={groupClassName}>
                <StyledLabel className="required__clz mt-3" htmlFor="name">
                    {t("Dashboard Link")}
                </StyledLabel>
                <StyledTextInput
                    data-testid="fidelity-franchise-zerosix-dashboard-link-input"
                    autoComplete="off"
                    id="Dashboard Link"
                    name="Dashboard Link"
                    placeholder={t("Write")}
                    type="text"
                    onChange={handleDashboardLinkChange}
                    className={classnames({ input__clz: inputDashboardLink })}
                    value={dashboardLink}
                />
                {inputDashboardLink ? (
                    <div style={ERROR_TEXT_STYLE}>
                        {t("Please enter a link to the dashboard")}
                    </div>
                ) : null}
            </AvGroup>
            <AvGroup className={groupClassName}>
                <StyledLabel className="required__clz mt-3" htmlFor="name">
                    {t("API key")}
                </StyledLabel>
                <StyledTextInput
                    data-testid="fidelity-franchise-zerosix-api-key-input"
                    autocomplete="off"
                    id="API key"
                    name="API key"
                    placeholder={t("Write")}
                    type="text"
                    onChange={handleApiKeyChange}
                    className={classnames({ input__clz: inputApiKey })}
                    value={apiKey}
                />
                {inputApiKey ? (
                    <div style={ERROR_TEXT_STYLE}>
                        {t("Please enter an API Key")}
                    </div>
                ) : null}
            </AvGroup>
        </React.Fragment>
    );
}
